import { create } from "zustand";

type State = {
  status: string;
  error: string;
  errorSSO: string;
  description: string;
  firmas: any[];
  apinetToken: string;
  signatureId: string;
  firstRun: boolean;
  isDarkMode: boolean;
  addinEnabled: boolean;
  forceLogin: boolean;
  setStatus: (status: string) => void;
  setError: (error: string) => void;
  setErrorSSO: (errorSSO: string) => void;
  setDescription: (description: string) => void;
  setFirmas: (firmas: any[]) => void;
  setApinetToken: (apinetToken: string) => void;
  setSignatureId: (signatureId: string) => void;
  setFirstRun: (firstRun: boolean) => void;
  setIsDarkMode: (isDarkMode: boolean) => void;
  setAddinEnabled: (addinEnabled: boolean) => void;
  logout: () => void;
  removeLocalStorageItem: (key: string) => void;
  removeRoamingSettingsItem: (key: string) => void;
  initialize: () => Promise<void>;
};

const getLocalStorageJson = (key: string) => {
  try {
    const item = getLocalStorageItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error al leer de localStorage: ${error}`);
    return null;
  }
};

const getLocalStorageItem = (key: string) => {
  try {
    const myPartitionKey = Office?.context?.partitionKey;
    if (myPartitionKey) {
      return localStorage.getItem(myPartitionKey + key);
    } else {
      return localStorage.getItem(key);
    }
  } catch (error) {
    console.error(`Error al leer de localStorage: ${error}`);
    return null;
  }
};

const setLocalStorageItem = (key: string, value: string) => {
  try {
    const myPartitionKey = Office?.context?.partitionKey;
    if (myPartitionKey) {
      localStorage.setItem(myPartitionKey + key, value);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error(`Error al escribir en localStorage: ${error}`);
  }
};

const removeLocalStorageItem = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error al borrar de localStorage: ${error}`);
  }
};

const setRoamingSettingsItem = (key: string, value: string) => {
  try {
    if (!Office?.context?.roamingSettings) return false;
    Office.context.roamingSettings.set(key, value);
    return Office.context.roamingSettings.saveAsync((asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Error al guardar en roamingSettings");
        return false;
      } else {
        return true;
      }
    });
  } catch (error) {
    console.error(`Error al escribir en roamingSettings: ${error}`);
    return false;
  }
};

const getRoamingSettingsItem = (key: string, initialValue?: string, callback?: (value: string) => any) => {
  try {
    const value = Office?.context?.roamingSettings?.get(key) ?? initialValue;
    if (callback) callback(value);
    return value;
  } catch (error) {
    console.error(`Error al leer de roamingSettings: ${error}`);
    return initialValue || null;
  }
};

const removeRoamingSettingsItem = (key: string) => {
  try {
    if (!Office?.context?.roamingSettings) return false;
    Office.context.roamingSettings.remove(key);
    return Office.context.roamingSettings.saveAsync((asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Error al guardar en roamingSettings");
        return false;
      } else {
        return true;
      }
    });
  } catch (error) {
    console.error(`Error al borrar de roamingSettings: ${error}`);
    return false;
  }
};

export const useStateStore = create<State>()((set) => ({
  status: "loading",
  description: "Cargando...",
  error: null,
  errorSSO: null,
  firmas: getLocalStorageJson("firmas") || [],
  apinetToken: "",
  signatureId: "",
  firstRun: getLocalStorageItem("firstRun") !== "false",
  isDarkMode:
    getLocalStorageItem("themeMode") === "dark"
      ? true
      : getLocalStorageItem("themeMode") === "light"
        ? false
        : window.matchMedia("(prefers-color-scheme: dark)").matches,
  addinEnabled: true,
  forceLogin: getLocalStorageItem("forceLogin") === "true",
  setStatus: (status: string) => set({ status }),
  setDescription: (description: string) => set({ description }),
  setError: (error: string) => set({ error }),
  setErrorSSO: (errorSSO: string) => set({ errorSSO }),
  setFirmas: (firmas: any[]) => {
    set({ firmas });
    setLocalStorageItem("firmas", JSON.stringify(firmas));
  },
  setApinetToken: (apinetToken: string) => {
    set({ apinetToken: apinetToken });
    setRoamingSettingsItem("apinetToken", apinetToken);
  },
  setSignatureId: (signatureId: string) => {
    set({ signatureId: signatureId });
    setRoamingSettingsItem("signatureId", signatureId);
    setRoamingSettingsItem("addinEnabled", "true");
  },
  setFirstRun: (firstRun: boolean) => {
    set({ firstRun: firstRun });
    setLocalStorageItem("firstRun", firstRun ? "true" : "false");
  },
  setIsDarkMode: (isDarkMode: boolean) => {
    set({ isDarkMode });
    setLocalStorageItem("themeMode", isDarkMode ? "dark" : "light");
  },
  setAddinEnabled: (addinEnabled: boolean) => {
    set({ addinEnabled: addinEnabled });
    setRoamingSettingsItem("addinEnabled", addinEnabled ? "true" : "false");
  },
  logout: () => {
    set({ apinetToken: "" });
    removeRoamingSettingsItem("apinetToken");
    set({ signatureId: "" });
    removeRoamingSettingsItem("signatureId");
    set({ forceLogin: true });
    setLocalStorageItem("forceLogin", "true");
    window.location.reload();
  },
  removeLocalStorageItem,
  removeRoamingSettingsItem,
  initialize: async () => {
    try {
      await Office.onReady();
      const apinetToken = getRoamingSettingsItem("apinetToken", "");
      const signatureId = getRoamingSettingsItem("signatureId", "");
      const addinEnabled = getRoamingSettingsItem("addinEnabled") !== "false";
      const forceLogin = getLocalStorageItem("forceLogin") === "true";
      const firstRun = getLocalStorageItem("firstRun") !== "false";
      const firmas = getLocalStorageJson("firmas") || [];

      set({
        apinetToken,
        signatureId,
        addinEnabled,
        forceLogin,
        firstRun,
        firmas,
      });
    } catch (error) {
      console.error("Error al inicializar el store con Office context", error);
    }
  },
}));
